'use client';

/**
 * Very basic translation component inspired by react-i18next.
 * The API is trying to be a subset of the react-i18next API, so we
 *   1. could replace it if needed
 *   2. can link to their documentation
 *
 * @see https://react.i18next.com/
 */
import type { ReactNode, JSX } from 'react';
import React, { createContext, useContext, useMemo } from 'react';
import { stripTranslationTags } from '@/app/components/translations/translationTags.ts';
import type { KeyMap } from '@/app/components/hotel-detail/common/translation.helper.ts';
import { getKeyGetter } from '@/app/components/hotel-detail/common/translation.helper.ts';

type Dictionary = {
  [key: string]: string;
};

type TranslationContextType = {
  translationDictionary: Dictionary;
  shoppingCardDictionary: Dictionary;
  tripCardDictionary: Dictionary;
  debug?: boolean;
};

type TranslationProviderProps = {
  translationDictionary: Dictionary;
  debug?: boolean;
  children: ReactNode;
};

export type TranslationFunction = (key: string, interpolationMap?: Dictionary) => string;

const TranslationContext = createContext<TranslationContextType>({
  translationDictionary: {},
  shoppingCardDictionary: {},
  tripCardDictionary: {},
  debug: false,
});

export function TranslationProvider({ translationDictionary, debug, children }: TranslationProviderProps) {
  const shoppingCardDictionary = useMemo(() => {
    const dict: Dictionary = {};
    Object.entries(translationDictionary).forEach(([key, value]) => {
      if (key.startsWith('widget.shopping_card')) {
        dict[key] = value;
      }
    });
    return dict;
  }, [translationDictionary]);

  const tripCardDictionary = useMemo(() => {
    const dict: Dictionary = {};
    Object.entries(translationDictionary).forEach(([key, value]) => {
      if (key.startsWith('widget.trip_card')) {
        dict[key] = value;
      }
    });
    return dict;
  }, [translationDictionary]);

  const contextValue = useMemo(
    () => ({ translationDictionary, shoppingCardDictionary, tripCardDictionary, debug }),
    [translationDictionary, shoppingCardDictionary, tripCardDictionary, debug]
  );

  return <TranslationContext.Provider value={contextValue}>{children}</TranslationContext.Provider>;
}

/**
 * usage analogue to `useTranslation` from `react-i18next`
 * @see https://react.i18next.com/latest/usetranslation-hook
 *
 * _note: other than i18next who interpolates `{{values}}` last minutes uses `__values__`_
 */
export function useTranslation() {
  const context = useContext(TranslationContext);

  if (context === undefined) {
    throw new Error('useTranslation must be within a TranslationContext');
  }

  const { translationDictionary, shoppingCardDictionary, tripCardDictionary, debug } = context;

  const t: TranslationFunction = (key: string, interpolationMap: { [key: string]: string } = {}) => {
    const translation = translationDictionary[key] || '';

    const interpolated = Object.entries(interpolationMap).reduce(
      // A function keeps a value literal: as a string, "$$" or "$&" in a hotel name would be
      // read as a replacement pattern.
      (acc, curr) => acc.replace(`__${curr[0]}__`, () => curr[1]),
      translation
    );

    if (debug && !translationDictionary[key]) {
      // eslint-disable-next-line no-console
      console.log('missedTranslationKeys', key);
    }

    return stripTranslationTags(interpolated);
  };

  /**
   * Translation for key map based on count parameter and given locale
   *
   * @param keyMap - map of copy keys which could be used
   *   singular - key for singular translation
   *   plural - key for plural translation
   *   pluralSpecial - optional key for specific plural translation
   * @param count - count of element decides which key should be used
   * @param interpolationMap - optional map of parameters
   */
  const translateBaseOnCount = (
    keyMap: KeyMap,
    count: number,
    locale: string,
    interpolationMap: { [key: string]: string } = {}
  ) => {
    const keyGetter = getKeyGetter(locale);
    const selectedKey = keyGetter(keyMap, count);
    return t(selectedKey, interpolationMap);
  };

  const translateWithElementPlaceholder = (key: string, elementKey: string, element: JSX.Element) => {
    const translation = translationDictionary[key] || key;

    const result = translation.split(`__${elementKey}__`).flatMap((part) => [part, element]);
    result.pop();
    return result;
  };

  const translateWithElements = (key: string, interpolationMap: { [key: string]: JSX.Element } = {}) => {
    const translation = translationDictionary[key] || key;

    // Create a regex that matches the placeholders precisely
    // This regex-based approach correctly handles special cases where placeholders
    // are separated by punctuation (e.g. vacation.select.facilities.disclaimer, "__hotelName__.__newLine__")
    // by matching each placeholder independently rather than splitting the string
    const regex = /__([a-zA-Z0-9_-]*)__/g;
    const parts: (string | JSX.Element)[] = [];

    let lastIndex = 0;

    // Find all matches and process them sequentially
    // Using let/const pattern to avoid assignment in while condition (ESLint no-cond-assign)
    let match = regex.exec(translation);
    while (match !== null) {
      // Add text before the placeholder
      if (match.index > lastIndex) {
        parts.push(translation.substring(lastIndex, match.index));
      }

      // Add the placeholder replacement or the placeholder itself if no replacement exists
      const placeholderKey = match[1];
      parts.push(interpolationMap[placeholderKey] || match[0]);

      lastIndex = regex.lastIndex;
      match = regex.exec(translation);
    }

    // Add any remaining text after the last placeholder
    if (lastIndex < translation.length) {
      parts.push(translation.substring(lastIndex));
    }

    return parts;
  };

  return {
    t,
    translateBaseOnCount,
    translateWithElementPlaceholder,
    translateWithElements,
    translationDictionary,
    shoppingCardDictionary,
    tripCardDictionary,
  };
}
